<template>
  <div class="dashboard-layout">
    <aside class="dashboard-layout__sidebar" :class="{ 'dashboard-layout__sidebar--collapsed': collapsed }">
      <slot name="sidebar">
        <DashboardSidebar :collapsed="collapsed" @toggle="collapsed = !collapsed" />
      </slot>
    </aside>
    
    <aside v-if="$slots.secondarySidebar" class="dashboard-layout__secondary-sidebar">
      <slot name="secondarySidebar" />
    </aside>
    
    <main class="dashboard-layout__main">
      <div 
        class="dashboard-layout__content"
        :class="{ 'dashboard-layout__content--with-secondary-sidebar': $slots.secondarySidebar }"
      >
        <slot name="content" />
      </div>
      
      <aside v-if="$slots.rightSidebar" class="dashboard-layout__right-sidebar">
        <slot name="rightSidebar" />
      </aside>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import DashboardSidebar from './DashboardSidebar.vue'

interface Props {
  defaultCollapsed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  defaultCollapsed: true
})

const collapsed = ref(props.defaultCollapsed)
</script>

<style lang="scss" scoped>
.dashboard-layout {
  display: flex;
  height: 100vh;
  background-color: var(--nvx-color-background-primary);
  color: var(--nvx-color-text-primary);

  &__sidebar {
    width: 101px;
    background-color: var(--nvx-color-background-tertiary);
    border-right: 1px solid var(--nvx-color-border-primary);
    transition: width 0.3s ease;
    flex-shrink: 0;

    &--collapsed {
      width: 101px;
    }

    &--open {
      transform: translateX(0);
    }
  }

  &__secondary-sidebar {
    width: 280px;
    background-color: #0C1625;
    flex-shrink: 0;
    overflow-y: auto;
    border-right: 1px solid var(--nvx-color-border-primary);
  }

  &__main {
    flex: 1;
    display: flex;
    min-width: 0;
  }

  &__content {
    flex: 1;
    padding-left: 12.44vw;
    padding-right: 17.08vw;
    padding-top: 7.5vh;
    padding-bottom: 3.1vh;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;

    &--with-secondary-sidebar {
      width: 100%;
      padding-left: 0;
      padding-right: 0;
    }
  }

  &__right-sidebar {
    width: 400px;
    height: 100vh;
    background-color: var(--nvx-color-background-secondary);
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  // Responsive breakpoints
  @media (max-width: 1024px) {
    &__right-sidebar {
      display: none;
    }
  }

  @media (max-width: 768px) {
    &__sidebar {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      z-index: var(--nvx-z-index-overlay);
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }
    
    &__content {
      padding: var(--nvx-spacing-4);
    }
  }
}
</style>
